import type {
  Category,
  CategoryReference,
  ChildcareAllowanceLevel,
  ChildcareUniverse,
  CleaningUniverse,
  IncomeLevel,
  MaritalStatus,
  MonthlyAllowance,
  Prestation,
  PrestationReference,
  ProGender,
  SubcategoryReference,
  Universe,
  UniverseElement,
  UniverseReference,
  YoungestChildAge,
} from '../types/commons';

// We use the ?. operator here because even if the type is Universe sometimes in production the universe is undefined
const isUniverse = (universe: Universe | undefined, reference: UniverseReference) => universe?.reference === reference;

const isUniverseReference = (universe: UniverseReference | undefined, reference: UniverseReference) =>
  universe === reference;

export const isCleaning = (universe?: Universe): universe is CleaningUniverse => isUniverse(universe, 'cleaning');

export const isChildcare = (universe?: Universe): universe is ChildcareUniverse => isUniverse(universe, 'childcare');

export const isMassage = (universe?: Universe): universe is Universe => isUniverse(universe, 'massage');

export const isBeauty = (universe?: Universe): universe is Universe => isUniverse(universe, 'beauty');

export const isHaircut = (universe?: Universe): universe is Universe => isUniverse(universe, 'haircut');

export const isSportsCoaching = (universe?: Universe): universe is Universe => isUniverse(universe, 'sports_coaching');

export const isCare = (universe?: Universe) => isCleaning(universe) || isChildcare(universe);

export const isWellness = (universe?: Universe): universe is Universe =>
  isMassage(universe) || isBeauty(universe) || isHaircut(universe) || isSportsCoaching(universe);

export const isHourlyBased = (universe?: Universe) => isCare(universe);

export const isCleaningReference = (reference?: UniverseReference) => isUniverseReference(reference, 'cleaning');

export const isChildcareReference = (reference?: UniverseReference) => isUniverseReference(reference, 'childcare');

export const isMassageReference = (reference?: UniverseReference) => isUniverseReference(reference, 'massage');

export const isBeautyReference = (reference?: UniverseReference) => isUniverseReference(reference, 'beauty');

export const isHaircutReference = (reference?: UniverseReference) => isUniverseReference(reference, 'haircut');

export const isSportsCoachingReference = (reference?: UniverseReference) =>
  isUniverseReference(reference, 'sports_coaching');

export const isCareReference = (reference?: UniverseReference) =>
  isCleaningReference(reference) || isChildcareReference(reference);

export const isWellnessReference = (reference?: UniverseReference) =>
  isMassageReference(reference) ||
  isBeautyReference(reference) ||
  isHaircutReference(reference) ||
  isSportsCoachingReference(reference);

export const isHourlyBasedReference = (reference?: UniverseReference) => isCareReference(reference);

const getAmount = (monthlyAllowances: MonthlyAllowance[], youngestChildAge: YoungestChildAge) => {
  const ageValue = monthlyAllowances.find((value) => value.youngest_child_age === youngestChildAge);
  return ageValue?.amount ?? null;
};

type GetChildcareAllowanceLevelsArgs = {
  universe: Universe | ChildcareUniverse;
  maritalStatus?: MaritalStatus;
  childrenCount?: number;
  youngestChildAge?: YoungestChildAge;
};

export const getChildcareAllowanceLevels = ({
  universe,
  maritalStatus,
  childrenCount,
  youngestChildAge = 'more_than_6',
}: GetChildcareAllowanceLevelsArgs): ChildcareAllowanceLevel[] | undefined => {
  if (!isChildcare(universe)) return undefined;

  const nbChildren = childrenCount && Math.min(4, childrenCount);
  const allowanceValues = universe.childcare_caf_allowance;
  const maritalStatusValues = allowanceValues && maritalStatus && allowanceValues[maritalStatus].total_children;
  const childrenValues = maritalStatusValues && nbChildren && maritalStatusValues[nbChildren];
  if (childrenValues) {
    const annualIncomeLevel = childrenValues.annual_income_level;
    return (Object.keys(annualIncomeLevel) as IncomeLevel[]).map((key) => ({
      type: key,
      minimumIncome: annualIncomeLevel[key].min_annual_income,
      maximumIncome: annualIncomeLevel[key].max_annual_income,
      amount: getAmount(annualIncomeLevel[key].monthly_allowance, youngestChildAge),
    }));
  }
  return undefined;
};

export const getTitleForCategory = (categoryReference: CategoryReference, universe: Universe) => {
  const universeCategory = universe.categories.find((c) => c.reference === categoryReference);

  return universeCategory ? universeCategory.title : '?';
};

export const filterCategoriesPrestations = (categories: Category[], filter: (prestation: Prestation) => boolean) =>
  categories
    .map((category) => ({
      ...category,
      subcategories: category.subcategories
        .map((subcategory) => ({
          ...subcategory,
          prestations: subcategory.prestations.filter(filter),
        }))
        .filter((subcategory) => subcategory.prestations.length > 0),
    }))
    .filter((category) => category.subcategories.length > 0);

export const enabledValues = <T extends UniverseElement>(values: T[]) => values.filter((value) => !value.disabled);

export const getDefaultGenderByUniverse = (universe?: UniverseReference): ProGender => {
  switch (universe) {
    case 'cleaning':
    case 'childcare':
    case 'beauty':
      return 'woman';
    case 'sports_coaching':
    case 'massage':
    case 'haircut':
      return 'man';
    default:
      return 'dont_care';
  }
};

type ReplacePrestationAndPriceInUniverseParams<T> = {
  universe: T;
  toReplace: {
    categoryReference: CategoryReference;
    subcategoryReference: SubcategoryReference;
    prestationReference: PrestationReference;
  };
  prestationOverride: Partial<Prestation>;
};

export const replacePrestationAndPriceInUniverse = <T extends Universe>({
  universe,
  toReplace,
  prestationOverride,
}: ReplacePrestationAndPriceInUniverseParams<T>): T => ({
  ...universe,
  categories: universe.categories.map((category) => {
    if (category.reference === toReplace.categoryReference) {
      return {
        ...category,
        subcategories: category.subcategories.map((subcategory) => {
          if (subcategory.reference === toReplace.subcategoryReference) {
            return {
              ...subcategory,
              prestations: subcategory.prestations.map((prestation) => {
                if (prestation.reference === toReplace.prestationReference) {
                  return {
                    ...prestation,
                    ...prestationOverride,
                  };
                }
                return prestation;
              }),
            };
          }
          return subcategory;
        }),
      };
    }
    return category;
  }),
});
